import { useCallback, useEffect, useMemo, useRef } from 'react';

import BpkCardList from '@skyscanner-internal/backpack-web/bpk-component-card-list';
import { parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { setTripDuration } from '../../actions/trip-duration-actions';
import { useScrollPosition } from '../../helpers/hooks/useScrollPosition';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { trackInteractionEvent } from '../../helpers/logging/trackInteractionEvent';
import {
  getCheapestCombinations,
  getLocalisationVars,
  getSelectedInboundMonth,
  getSelectedOutboundMonth,
  getTripDuration,
} from '../../selectors';

import { DurationAutoselectCard } from './DurationAutoselectCard';

import type { CheapestCombination } from '../../selectors/cheapestDatesSelectors';
import type { State, TripDuration } from '../../selectors/types';

const LAYOUTS = {
  grid: 'grid',
  stack: 'stack',
  row: 'row',
  rail: 'rail',
} as const;

type CombosByDuration = Record<TripDuration, CheapestCombination | null>;
const EMPTY_COMBOS: CombosByDuration = {
  weekend: null,
  longWeekend: null,
  7: null,
  14: null,
};

const buildDate = (month: Date, dayIndex: number): Date =>
  parseISO(
    `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}-${String(dayIndex + 1).padStart(2, '0')}`,
  );

const formatDateRange = (
  outMonth: Date,
  outIdx: number,
  inMonth: Date,
  inIdx: number,
  locale?: string,
): string => {
  const userLocale =
    locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-GB');

  const withMonth = (date: Date) =>
    `${date.toLocaleString(userLocale, { month: 'short' })} ${date.getDate()}`;
  const dayOnly = (date: Date) => date.getDate().toString();

  const sameMonth = outMonth.getMonth() === inMonth.getMonth();
  const outDate = buildDate(outMonth, outIdx);
  const inDate = buildDate(inMonth, inIdx);

  return `${withMonth(outDate)} - ${sameMonth ? dayOnly(inDate) : withMonth(inDate)}`;
};

type DurationAutoselectCardListProps = {
  onScrollPositionChange: (isAtStart: boolean, isAtEnd: boolean) => void;
};

export const DurationAutoselectCardList = ({
  onScrollPositionChange,
}: DurationAutoselectCardListProps) => {
  const translate = useTranslation();
  const { formatCurrency } = useLocalisation();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  // Track scroll position of BpkCardList's internal rail element
  const { isAtEnd, isAtStart } = useScrollPosition(
    containerRef,
    '[class*="bpk-card-list-row-rail__rail"]',
  );

  // Notify parent of scroll position changes
  useEffect(() => {
    onScrollPositionChange(isAtStart, isAtEnd);
  }, [isAtStart, isAtEnd, onScrollPositionChange]);

  // Redux selectors
  const tripDuration = useSelector(getTripDuration);
  const outboundMonth = useSelector(getSelectedOutboundMonth);
  const inboundMonth = useSelector(getSelectedInboundMonth);
  const locale = useSelector((state: State) => {
    const localisationVars = getLocalisationVars(state);
    return localisationVars.locale;
  });
  const combosByDuration = useSelector(getCheapestCombinations) ?? EMPTY_COMBOS;

  // Duration configuration with translated labels
  const durationConfigs = useMemo(
    () => [
      {
        duration: 'weekend' as TripDuration,
        label: translate('mv_cheapestDates_weekend'),
      },
      {
        duration: 'longWeekend' as TripDuration,
        label: translate('mv_cheapestDates_long_weekend'),
      },
      {
        duration: 7 as TripDuration,
        label: translate('mv_cheapestDates_one_week_trip'),
      },
      {
        duration: 14 as TripDuration,
        label: translate('mv_cheapestDates_two_week_trip'),
      },
    ],
    [translate],
  );

  const handleDurationSelect = useCallback(
    (duration: TripDuration) => {
      dispatch(setTripDuration(duration));
    },
    [dispatch],
  );

  const cardsToRender = useMemo(() => {
    const cards: JSX.Element[] = [];

    durationConfigs.forEach((cfg) => {
      const combo = combosByDuration[cfg.duration];
      if (!combo || !outboundMonth || !inboundMonth) return;

      const handleCardClick = () => {
        handleDurationSelect(cfg.duration);
        trackInteractionEvent({
          name: 'Combination_Card_Selected',
          strings: {
            tripDuration: cfg.duration.toString(),
          },
        });
      };
      cards.push(
        <DurationAutoselectCard
          key={String(cfg.duration)}
          durationString={cfg.label}
          dateString={formatDateRange(
            outboundMonth,
            combo.outboundIndex,
            inboundMonth,
            combo.inboundIndex,
            locale,
          )}
          priceString={formatCurrency(combo.price)}
          onClick={handleCardClick}
          selected={tripDuration === cfg.duration}
        />,
      );
    });

    return cards;
  }, [
    durationConfigs,
    combosByDuration,
    tripDuration,
    outboundMonth,
    inboundMonth,
    locale,
    formatCurrency,
    handleDurationSelect,
  ]);

  useEffect(() => {
    if (cardsToRender.length > 0) {
      trackInteractionEvent({
        name: 'Combination_Card_List_Rendered',
        bools: {
          hasWeekend: combosByDuration.weekend !== null,
          hasLongWeekend: combosByDuration.longWeekend !== null,
          hasOneWeek: combosByDuration[7] !== null,
          hasTwoWeek: combosByDuration[14] !== null,
        },
      });
    }
  }, [cardsToRender.length, combosByDuration]);

  if (cardsToRender.length === 0) {
    return null;
  }

  return (
    <div className="card-list-container" ref={containerRef}>
      <BpkCardList
        layoutDesktop={LAYOUTS.row}
        layoutMobile={LAYOUTS.rail}
        initiallyShownCardsDesktop={4}
        initiallyShownCardsMobile={3}
        cardList={cardsToRender}
      />
    </div>
  );
};
